import { QuestionAnswerMap } from '@care-portals/api-interfaces';

const questionsModel = [
  {
    field: 'antiNausea_educationalInfo',
    label: 'Before you start: When to seek urgent care for nausea',
    type: 'select',
    educationalSlide:
      'Nausea is a common but nonspecific symptom that can accompany a wide range of conditions - from benign, self‑limited issues such as viral gastroenteritis, food poisoning, motion sickness, or medication side effects, to more serious disorders including bowel obstruction, intracranial pressure changes, cardiac ischemia, or metabolic disturbances.\n\nWhile most cases resolve with conservative care and targeted anti‑emetics, certain "red‑flag" features (vomit containing blood or coffee‑ground material, sudden severe abdominal pain, stiff neck or high fever with headache, new confusion or severe dizziness, or rapid clinical worsening, inability to keep down fluids, scant urine output, signs of dehydration) may indicate a potentially serious underlying process requiring urgent in‑person assessment.\n\nIf you develop any of these warning signs or your symptoms escalate quickly despite initial treatment, seek immediate evaluation by a qualified healthcare provider or emergency department.',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I understand the above information and do NOT wish to proceed',
          class: 'option-decline',
          key: 'do_not_proceed'
        },
        {
          value: 'I understand the above information and would like to proceed',
          class: 'option-consent',
          key: 'proceed'
        }
      ]
    }
  },
  {
    field: 'antiNausea_sexAssignedAtBirth',
    label: 'What was your sex assigned at birth?',
    extras: { sendToBeluga: false },
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Male',
          key: 'male'
        },
        {
          value: 'Female',
          key: 'female'
        }
      ]
    }
  },
  {
    field: 'antiNausea_pregnancyStatus',
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes',
          key: 'yes'
        },
        {
          value: 'No',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'antiNausea_consentPregnancy',
    label: 'Consent (pregnancy):',
    type: 'select',
    educationalSlide: `Read the following for more information about this product and its potential side effects:
It is not safe to take these medications while pregnant or breastfeeding. The FDA advises that these medications may pose a risk to a developing fetus. Oral contraceptives alone may not be effective, as the medication can reduce their effectiveness. The FDA specifically recommends continuing oral contraception alongside a barrier method (like condoms) for the first month after starting a weight loss medication and for the first month after any dose increase. Alternatively, you can switch to a non-oral contraceptive method (such as an IUD or implant) before beginning the medication. After stopping the medication, you should continue using a backup method, such as condoms, for two months to ensure the medication has fully cleared your system before trying to conceive. Additionally, its safety during breastfeeding is unknown, so if you are nursing, consult your doctor to explore safer weight loss options.
`,
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'do_not_continue'
        },
        {
          value:
            'I acknowledge that I have read and understood the above information',
          class: 'option-consent',
          key: 'acknowledge'
        }
      ]
    }
  },
  {
    field: 'antiNausea_height',
    label: 'What is your height in feet and inches?',
    type: 'info',
    answer: {
      type: 'text',
      name: 'antiNausea_height'
    }
  },
  {
    field: 'antiNausea_weight',
    label: 'What is your weight in pounds?',
    type: 'info',
    answer: {
      type: 'text',
      name: 'antiNausea_weight'
    }
  },
  {
    field: 'antiNausea_duration',
    label:
      'How long have you been experiencing nausea during this specific episode?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: '< 6 hours',
          key: 'less_than_6_hours'
        },
        {
          value: '6 - 24 hours',
          key: '6_to_24_hours'
        },
        {
          value: '2-7 days',
          key: '2_to_7_days'
        },
        {
          value: '> 7 days',
          key: 'more_than_7_days'
        }
      ]
    }
  },
  {
    field: 'antiNausea_trigger',
    label:
      'What triggered your current nausea episode in your opinion? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'Unsure',
          uncheckOthers: true,
          key: 'unsure'
        },
        {
          value: 'Medication side-effect',
          key: 'medication_side_effect'
        },
        {
          value: 'Viral illness',
          key: 'viral_illness'
        },
        {
          value: 'Food poisoning',
          key: 'food_poisoning'
        },
        {
          value: 'Migraine aura',
          key: 'migraine_aura'
        },
        {
          value: 'Motion/sensory',
          key: 'motion_sensory'
        },
        {
          value: 'Alcohol',
          key: 'alcohol'
        },
        {
          value: 'Chemotherapy',
          key: 'chemotherapy'
        },
        {
          value: 'Recent surgery/anesthesia',
          key: 'recent_surgery_anesthesia'
        },
        {
          value: 'Anxiety/stress',
          key: 'anxiety_stress'
        },
        {
          value: 'Other',
          key: 'other'
        }
      ]
    }
  },
  {
    field: 'antiNausea_fluidsKeptDown',
    label: 'Have you kept down fluids in the past 24 hours?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes - with no problem',
          key: 'yes_no_problem'
        },
        {
          value: 'Yes - but only small sips',
          key: 'yes_small_sips'
        },
        {
          value: 'No - everything comes back up',
          key: 'no_everything_comes_back_up'
        }
      ]
    }
  },
  {
    field: 'antiNausea_medicationCausingReaction',
    label: 'Which medication do you suspect is causing this reaction?',
    type: 'info',
    answer: {
      type: 'text',
      name: 'antiNausea_medicationCausingReaction'
    }
  },
  {
    field: 'antiNausea_otherCause',
    label: 'Tell us about your other cause of your nausea',
    type: 'info',
    answer: {
      type: 'text',
      name: 'antiNausea_otherCause'
    }
  },
  {
    field: 'antiNausea_redFlagSymptoms',
    label:
      'Safety is our number one priority for you. Do you have any of these "red-flag" symptoms today?',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'Vomit with blood or coffee-ground appearance',
          key: 'vomit_with_blood'
        },
        {
          value: 'Severe, sudden abdominal pain',
          key: 'severe_abdominal_pain'
        },
        {
          value: 'Stiff neck or severe headache with fever',
          key: 'stiff_neck_headache_fever'
        },
        {
          value: 'Confusion, slurred speech, or severe dizziness',
          key: 'confusion_slurred_speech_dizziness'
        },
        {
          value: 'None of the above',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'antiNausea_medicationsCheck',
    label: 'Do you take any of the following medications?',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'Cisapride',
          key: 'cisapride'
        },
        {
          value:
            'Antifungals like fluconazole, itraconazole, ketoconazole, posaconazole, voriconazole',
          key: 'antifungals'
        },
        {
          value: 'Dronedarone',
          key: 'dronedarone'
        },
        {
          value: 'Pimozide',
          key: 'pimozide'
        },
        {
          value: 'Thioridazine',
          key: 'thioridazine'
        },
        {
          value: 'Apomorphine',
          key: 'apomorphine'
        },
        {
          value: 'None of these apply to me',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'antiNausea_allergicReactionAntiNausea',
    label:
      'Have you ever had an allergic reaction or severe adverse effect from anti-nausea medications (for example, ondansetron/Zofran)?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes',
          key: 'yes'
        },
        {
          value: 'No',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'antiNausea_prolongedQT',
    label:
      'Have you ever been told that you have a prolonged QT interval on your EKG?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes',
          key: 'yes'
        },
        {
          value: 'No',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'antiNausea_irregularHeartbeat',
    label:
      'Do you frequently experience an irregular heartbeat or heart palpitations?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes',
          key: 'yes'
        },
        {
          value: 'No',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'antiNausea_finalConsent',
    label: 'Consent:',
    type: 'select',
    educationalSlide: `You have reported experiencing nausea and/or vomiting, conditions that can significantly affect your comfort, daily activities, and overall quality of life. Ondansetron (Zofran) is a medication commonly prescribed to help prevent or relieve these symptoms by blocking certain chemical receptors in the body responsible for inducing nausea and vomiting.

The use of ondansetron may:
➢ Reduce or prevent episodes of nausea and vomiting
➢ Improve comfort and quality of life
➢ Help you maintain proper nutrition and hydration

While ondansetron is generally well-tolerated, possible side effects may include:
➢ Headache
➢ Constipation
➢ Dizziness or lightheadedness
➢ Fatigue or drowsiness
➢ Dry mouth
➢ Rare but serious side effects such as changes in heart rhythm (including QT prolongation) or severe allergic reactions (rash, itching, swelling)

You may need to avoid ondansetron or use it with caution if you:
➢ Have a known heart rhythm disorder, such as congenital long QT syndrome
➢ Are currently taking other medications that can affect the heart's rhythm or serotonin levels
➢ Have a history of severe allergic reactions to ondansetron or similar medications

Alternative treatment options for nausea and vomiting include:
➢ Dietary modifications (small, frequent meals; avoiding triggers)
➢ Ginger (such as ginger tea or candies)
➢ Acupressure or acupuncture
➢ Other prescription or over-the-counter anti-nausea medications
➢ Behavioral techniques (relaxation exercises, controlled breathing)

I understand the potential benefits, risks, and possible side effects of using ondansetron. I have been informed about alternative treatment options. I agree to inform my healthcare provider of any side effects or adverse reactions I may experience. I understand that this consent is voluntary and that I can withdraw from treatment at any time.

By selecting below, I consent to the use of ondansetron for the treatment of my nausea and/or vomiting.
`,
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the information and I DO NOT wish to proceed',
          class: 'option-decline',
          key: 'do_not_proceed'
        },
        {
          value:
            'I have read and understand the information and I wish to proceed',
          class: 'option-consent',
          key: 'proceed'
        }
      ]
    }
  },
  {
    field: 'antiNausea_complete',
    isLast: true,
    label: `Thank you for completing this questionnaire, please select "Finish" to submit`,
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    class: 'ineligible',
    field: 'antiNausea_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    extras: { sendToBeluga: false },
    blockQuestionFlow: true,
    answer: {},
    type: 'info',
    isLast: true
  }
] as const;

export type AntiNauseaQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof questionsModel>
>;
export default questionsModel;
